import type { VitalDef, VitalView } from './vitals.js';
import type { C311Category, C311Summary, LayerState } from '@vitals/shared';

const rangePart = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', timeZone: 'UTC',
});
const throughPart = new Intl.DateTimeFormat('en-US', {
  month: 'short', day: 'numeric', year: 'numeric',
  hour: 'numeric', minute: '2-digit', timeZone: 'UTC',
});
const PRESERVED_ACRONYMS = new Set(['DEP', 'DOB', 'DOT', 'DSNY', 'HPD', 'NYC', 'NYPD']);

function categoryLabel(value: string): string {
  if (value !== value.toUpperCase()) return value;
  return value.toLowerCase().replace(/[a-z]+/g, (word) => {
    const upper = word.toUpperCase();
    return PRESERVED_ACRONYMS.has(upper) ? upper : `${word[0]!.toUpperCase()}${word.slice(1)}`;
  });
}

/** Treat a Socrata floating timestamp as display-only New York wall time. */
function floatingDate(value: unknown): Date | null {
  if (typeof value !== 'string' || value === '') return null;
  const date = new Date(`${value.replace(/Z$/, '')}Z`);
  return Number.isFinite(date.getTime()) ? date : null;
}

function rangeLabel(start: string, end: string): string {
  const startDate = floatingDate(start);
  const endDate = floatingDate(end);
  if (!startDate || !endDate) return `${start} – ${end}`;
  return `${rangePart.format(startDate)} – ${rangePart.format(endDate)}`;
}

function detailRows(summary: C311Summary): NonNullable<VitalView['details']> {
  if (
    typeof summary.windowEnd !== 'string'
    || !Array.isArray(summary.timeBuckets)
    || !Array.isArray(summary.categories)
  ) return [];
  const buckets = Array.isArray(summary.timeBuckets) ? summary.timeBuckets : [];
  const maxBucket = Math.max(0, ...buckets.map(({ count }) => Number.isFinite(count) ? count : 0));
  const bars: NonNullable<VitalView['details']> = buckets.map((bucket) => ({
    kind: 'bar',
    label: rangeLabel(bucket.start, bucket.end),
    value: Math.max(0, bucket.count).toLocaleString('en-US'),
    frac: maxBucket > 0 ? Math.max(0, bucket.count) / maxBucket : 0,
  }));

  const categories = (Array.isArray(summary.categories) ? summary.categories : [])
    .filter((category): category is C311Category =>
      typeof category.name === 'string' && Number.isFinite(category.count) && category.count > 0)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const top = categories.slice(0, 5);
  const otherCount = categories.slice(5).reduce((sum, category) => sum + category.count, 0);
  const shown = otherCount > 0 ? [...top, { name: 'Other', count: otherCount }] : top;
  const total = Number.isFinite(summary.count) && summary.count > 0 ? summary.count : 0;
  const categoryRows: NonNullable<VitalView['details']> = shown.map((category) => {
    const frac = total > 0 ? Math.min(1, category.count / total) : 0;
    return {
      kind: 'category',
      label: categoryLabel(category.name),
      value: category.count.toLocaleString('en-US'),
      percent: `${Math.round(frac * 100)}%`,
      frac,
    };
  });

  const end = floatingDate(summary.windowEnd);
  const through = end ? throughPart.format(end) : (summary.windowEnd || 'unknown');
  return [...bars, ...categoryRows, { kind: 'note', text: `Through ${through} ET` }];
}

/**
 * Vital selector for NYC 311 complaints.
 *
 * - value: formatted complaint count ("1,234")
 * - label: "complaints"
 * - topType is rendered via textContent in the panel so it is XSS-safe
 */
export const complaintsVital: VitalDef = {
  id: '311',
  layerId: 'c311',

  select: (s: LayerState | undefined): VitalView => {
    const down = !s || s.status === 'down' || s.summary == null;

    if (down) {
      return {
        value: '—',
        label: '311 requests',
        down: true,
        stale: false,
      };
    }

    const summary = s.summary as C311Summary;
    const { count } = summary;

    const value = count.toLocaleString('en-US');
    const details = detailRows(summary);

    return {
      value,
      label: '311 requests',
      down: false,
      stale: s.status === 'stale',
      ...(details.length > 0 && {
        detailTitle: '311 complaints · 48 hours',
        details,
      }),
    };
  },
};
